import { isCancellationForm } from '@/components/Form/CancellationRetention/cancellationRetentionHelper';
import { type FormValues } from '@/components/Form/types';
import { acceptMarketingOptIn } from '@/helper/cdcOptIn';
import formHandler from '@/helper/form';
import {
  GLOBAL_POPUP_EVENTS,
  dispatchGlobalPopupEvent,
  markPendingCancellationPopup,
} from '@/helper/globalPopupEvents';
import { isHoneypotFilled } from '@/helper/honeypot';
import type { ServerFieldErrors } from '@/helper/validation/serverFieldErrors';
import type { FormStoryblok, NewLineStoryblok } from '@/types/component-types-sb';
import type { NavigateOptions } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { type SetStateAction } from 'react';

import { type FormField, formFieldsResolver, getActiveFormValues } from './formHelper';

export interface MarketingOptInData {
  email: string;
  title: string;
  firstname: string;
  lastname: string;
  salutation: string;
  marketing_opt_in: string;
}

export const showData = (
  values: FormValues,
  form: FormStoryblok,
  setSubmittedValues: (value: SetStateAction<FormField[]>) => void
) => {
  if (!form.content) return;
  const data = formFieldsResolver(form.content, values, [], form);
  setSubmittedValues(data);
};

export const handleSuccessOrRedirect = (
  blok: FormStoryblok | NewLineStoryblok,
  routerPush: (href: string, options?: NavigateOptions) => void,
  setIsSuccessfullySubmitted: (value: SetStateAction<boolean>) => void
): boolean => {
  const showsCancellationPopup = blok.component === 'Form' && isCancellationForm(blok);

  if (!!blok.targetPage && blok.targetPage.cached_url?.length > 0) {
    // The form unmounts on navigation, so the popup is dispatched on the target page.
    if (showsCancellationPopup) {
      markPendingCancellationPopup();
    }
    routerPush(`/${blok.targetPage.cached_url}`);
    return true;
  }

  if (showsCancellationPopup) {
    dispatchGlobalPopupEvent(GLOBAL_POPUP_EVENTS.CANCELLATION_FORM);
  }
  setIsSuccessfullySubmitted(true);
  return false;
};

export const HONEYPOT_FIELD = 'homepage';

export const handleSubmitFinal = async (
  values: FormValues,
  blok: FormStoryblok,
  setIsLoading: (value: SetStateAction<boolean>) => void,
  setIsSendFailure: (value: SetStateAction<boolean>) => void,
  setIsSuccessfullySubmitted: (value: SetStateAction<boolean>) => void,
  routerPush: (href: string, options?: NavigateOptions) => void,
  setFlashFormData: ((formData: FormField[]) => void) | undefined,
  trackPurchase: (() => void) | undefined,
  pathname: string | undefined,
  formSourceStoryUuid: string
): Promise<{ fieldErrors?: ServerFieldErrors }> => {
  setIsLoading(true);

  let hasRedirect = false;

  // Honeypot check: if the hidden 'homepage' field is filled, treat as successful submission
  // and execute the same targetPage redirect logic (if configured)
  if (isHoneypotFilled(values as Record<string, unknown>, HONEYPOT_FIELD)) {
    setIsSendFailure(false);
    hasRedirect = handleSuccessOrRedirect(blok, routerPush, setIsSuccessfullySubmitted);
    if (!hasRedirect) {
      setIsLoading(false);
    }
    return {};
  }

  try {
    if (!blok?.action || blok.action.length === 0) {
      throw new Error('No action configuration available.');
    }

    const activeValues = getActiveFormValues(blok, values);

    // first we create a sendable form data list
    const formData = new FormData();

    for (const key in activeValues) {
      if (!key || key.length === 0) {
        continue;
      }
      const value = activeValues[key];
      if (Array.isArray(value)) {
        if (value.length === 0) {
          formData.append(key, '');
        }
        for (let index = 0; index < value.length; index++) {
          const item = value.at(index);
          if (item instanceof File) {
            formData.append(key, item, item.name);
          } else {
            formData.append(key, String(item));
          }
        }
      } else {
        formData.append(key, String(value));
      }
    }

    let resp = '';

    const formFieldsResolved = formFieldsResolver(blok.content!, activeValues, [], blok).map((field) => {
      const submittableField = { ...field };
      delete submittableField.fieldValueLabels;
      return submittableField;
    });

    // Always submit the base formID; server will append a trusted suffix after signature validation
    const formID = blok.id;
    formData.append('formID', formID);
    // Pass the dynamic suffix field id to the server so it can read the proper query param name
    if (blok.suffix) {
      formData.append('suffixFieldId', blok.suffix);
    }

    const hidden = (fieldId: string, fieldValue: string) => ({
      fieldId,
      fieldValue,
      fieldValueLabel: '',
      fieldLabel: '',
      hiddenField: true,
    });

    // Attach tracking/session identifiers: always include swd_vkn and swd_campaign
    let swdVknVal = '';
    let swdCampaignVal = '';
    try {
      if (typeof window !== 'undefined') {
        swdVknVal = sessionStorage.getItem('swd_vkn') ?? '';
        swdCampaignVal = sessionStorage.getItem('swd_campaign') ?? '';
      }
    } catch (e) {
      console.error('error accessing sessionStorage for swd_vkn or swd_campaign', e);
    }

    formFieldsResolved.push(hidden('swd_vkn', swdVknVal));
    formFieldsResolved.push(hidden('swd_campaign', swdCampaignVal));

    setFlashFormData && setFlashFormData(formFieldsResolved);

    try {
      const result = await formHandler(formData, formFieldsResolved, blok._uid, {
        pathname,
        formSourceStoryUuid,
      });

      if (result.fieldErrors && Object.keys(result.fieldErrors).length > 0) {
        setIsSendFailure(false);
        return { fieldErrors: result.fieldErrors };
      }

      resp = result.message;

      if (!resp) {
        console.error('[form submit] empty message from formHandler', {
          formUID: blok._uid,
          formID: blok.id,
          hasFieldErrors: Boolean(result.fieldErrors),
        });
      }

      if (resp) {
        // get all form fields as map
        const formFieldMap: { [key: string]: FormField } = formFieldsResolved.reduce(
          (map, formField) => {
            map[formField.fieldId] = formField;
            return map;
          },
          {} as { [key: string]: FormField }
        );

        // Handle Marketing Opt-In, if present
        if (formFieldMap.hasOwnProperty('marketing_opt_in') && !!formFieldMap['marketing_opt_in'].fieldValue) {
          // get everything required by the marketing opt in stuff
          const marketingOptInData: MarketingOptInData = {
            email: '',
            firstname: '',
            lastname: '',
            salutation: '',
            title: '',
            marketing_opt_in: '',
          };

          for (const key in marketingOptInData) {
            if (marketingOptInData.hasOwnProperty(key) && formFieldMap.hasOwnProperty(key)) {
              const formField = formFieldMap[key];
              marketingOptInData[key as keyof MarketingOptInData] =
                !!formField?.fieldValueLabel && formField.fieldValueLabel.length > 0
                  ? formField.fieldValueLabel
                  : (formField?.fieldValue ?? '');
            }
          }

          await acceptMarketingOptIn(
            marketingOptInData.email,
            marketingOptInData.title,
            marketingOptInData.firstname,
            marketingOptInData.lastname,
            marketingOptInData.salutation,
            marketingOptInData.marketing_opt_in
          );
        }

        // Handle success-message OR redirect to thank you page
        hasRedirect = handleSuccessOrRedirect(blok, routerPush, setIsSuccessfullySubmitted);

        trackPurchase && trackPurchase();

        setIsSendFailure(false);
      } else {
        throw new Error('Submission did not succeed.');
      }
    } catch (e) {
      console.error('[form submit] formHandler failed', {
        formUID: blok._uid,
        formID: blok.id,
        error: e,
      });
      setIsSendFailure(true);
      throw new Error(`Server responded with status: ${resp}`);
    }
  } catch (error) {
    console.error('Submission error:', error);
    setIsSendFailure(true);
  } finally {
    if (!hasRedirect) {
      setIsLoading(false);
    }
  }

  return {};
};
